import Pusher, { Channel } from "pusher-js";
import { skedApi, getToken, baseUrl } from "api/_skedApi";
import { PolicyEnforcementStatus } from "shared/types/accounts";
import {
  ColComment,
  ColCommentVisibility,
} from "../components/Collaboration/types";
import config from "../config";

// Alternative dev/test credentials (EU cluster):
// const PUSHER_API_KEY = "908d239ded2559d5ca34";
// const PUSHER_CLUSTER = "eu";

const PUSHER_API_KEY = config.pusherApiKey;
const PUSHER_CLUSTER = config.pusherCluster;
const SUBSCRIPTION_TIMEOUT_MS = 10_000;

export const PUSHER_EVENTS = {
  COMMENT_ADDED: "COMMENT_ADDED",
  COMMENT_EDITED: "COMMENT_EDITED",
  COMMENTS_READ: "COMMENTS_READ",
  STATUS_CHANGED: "STATUS_CHANGED",
  INBOX_MESSAGE_ADDED: "INBOX_MESSAGE_ADDED",
  INBOX_MESSAGE_DELETE_STATUS: "INBOX_MESSAGE_DELETE_STATUS",
  INBOX_MESSAGE_REMOVED: "INBOX_MESSAGE_REMOVED",
  INBOX_COLLABORATOR_TYPING: "INBOX_COLLABORATOR_TYPING",
  INBOX_MESSAGE_REACTION: "INBOX_MESSAGE_REACTION",
  ACCOUNT_BLOCKED: "EnforcementAccountBlocked",
  ACCOUNT_UNBLOCKED: "EnforcementAccountUnblocked",
  WARNING_RECEIVED: "EnforcementWarningReceived",
};

export class CollaborationApi {
  pusher?: Pusher;

  async createPusher(forceFetchingToken = false): Promise<Pusher> {
    const token = await getToken(forceFetchingToken);

    return new Pusher(PUSHER_API_KEY!, {
      authEndpoint: `${baseUrl}/collaboration-channels/auth`,
      cluster: PUSHER_CLUSTER!,
      forceTLS: true,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
  }

  private setPusherToken(token: string) {
    const pusherConfig = (
      this.pusher as
        | (Pusher & {
            config?: { auth?: { headers?: { Authorization?: string } } };
          })
        | undefined
    )?.config;

    if (pusherConfig?.auth?.headers) {
      pusherConfig.auth.headers.Authorization = "Bearer ".concat(token);
    } else {
      console.warn(
        "CollaborationApi: could not update Pusher auth token — internal config shape may have changed"
      );
    }
  }

  private async getOrCreatePusher(forceFetchingToken = false) {
    if (!this.pusher) {
      this.pusher = await this.createPusher(forceFetchingToken);
    } else if (forceFetchingToken) {
      const token = await getToken(true);
      this.setPusherToken(token);
    }

    return this.pusher;
  }

  private isRetryableSubscriptionError(error: unknown) {
    const status =
      typeof error === "object" && error && "status" in error
        ? (error as { status?: number }).status
        : undefined;

    return status === 401 || status === 403;
  }

  private toSubscriptionError(channelName: string, error: unknown) {
    const status =
      typeof error === "object" && error && "status" in error
        ? ` (status ${(error as { status?: number }).status})`
        : "";

    return new Error(
      `Failed to subscribe to collaboration channel ${channelName}${status}`
    );
  }

  private waitForSubscription(
    channel: Channel,
    retryWithFreshToken: boolean,
    channelName: string
  ): Promise<Channel> {
    if ((channel as Channel & { subscribed?: boolean }).subscribed) {
      return Promise.resolve(channel);
    }

    return new Promise((resolve, reject) => {
      const handleSuccess = () => {
        cleanup();
        resolve(channel);
      };

      const handleError = async (error: unknown) => {
        cleanup();

        if (retryWithFreshToken && this.isRetryableSubscriptionError(error)) {
          try {
            const retryChannel = await this.subscribeInternal(
              channelName,
              true,
              false
            );
            resolve(retryChannel);
            return;
          } catch (retryError) {
            reject(retryError);
            return;
          }
        }

        reject(this.toSubscriptionError(channelName, error));
      };

      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }

        channel.unbind("pusher:subscription_succeeded", handleSuccess);
        channel.unbind("pusher:subscription_error", handleError);
      };

      timeoutId = setTimeout(() => {
        cleanup();
        reject(
          new Error(
            `Timed out waiting to subscribe to collaboration channel ${channelName} after ${SUBSCRIPTION_TIMEOUT_MS}ms`
          )
        );
      }, SUBSCRIPTION_TIMEOUT_MS);

      channel.bind("pusher:subscription_succeeded", handleSuccess);
      channel.bind("pusher:subscription_error", handleError);
    });
  }

  private async subscribeInternal(
    channelName: string,
    forceFetchingToken = false,
    retryWithFreshToken = true
  ): Promise<Channel> {
    const pusher = await this.getOrCreatePusher(forceFetchingToken);
    if (forceFetchingToken) {
      pusher.unsubscribe(channelName);
    }
    const channel = pusher.subscribe(channelName);

    return this.waitForSubscription(channel, retryWithFreshToken, channelName);
  }

  async subscribe(
    channel: string,
    forceFetchingToken?: boolean
  ): Promise<Channel> {
    const pusher = await this.getOrCreatePusher(forceFetchingToken);
    if (forceFetchingToken) {
      pusher.unsubscribe(channel);
    }
    return pusher.subscribe(channel);
  }

  async subscribeToInboxChannelCallbacks(
    adminId: string,
    callbacks: {
      onMessageAdded?: (event: unknown) => void;
      onMessageDeleteStatus?: (event: unknown) => void;
      onMessageReaction?: (event: unknown) => void;
    }
  ): Promise<() => void> {
    const channelName = `private-inbox-${adminId}`;
    const channel = await this.subscribeInternal(channelName, true, true);
    const handleMessageAdded = (event: unknown) => {
      callbacks.onMessageAdded?.(event);
    };
    const handleMessageDeleteStatus = (event: unknown) => {
      callbacks.onMessageDeleteStatus?.(event);
    };
    const handleMessageReaction = (event: unknown) => {
      callbacks.onMessageReaction?.(event);
    };

    channel.bind(PUSHER_EVENTS.INBOX_MESSAGE_ADDED, handleMessageAdded);
    channel.bind(
      PUSHER_EVENTS.INBOX_MESSAGE_DELETE_STATUS,
      handleMessageDeleteStatus
    );
    channel.bind(PUSHER_EVENTS.INBOX_MESSAGE_REACTION, handleMessageReaction);

    return () => {
      channel.unbind(PUSHER_EVENTS.INBOX_MESSAGE_ADDED, handleMessageAdded);
      channel.unbind(
        PUSHER_EVENTS.INBOX_MESSAGE_DELETE_STATUS,
        handleMessageDeleteStatus
      );
      channel.unbind(
        PUSHER_EVENTS.INBOX_MESSAGE_REACTION,
        handleMessageReaction
      );
      this.unsubscribeChannel(channelName);
    };
  }

  async subscribeToCollabChannelCallbacks(
    adminId: string,
    channelId: string,
    callbacks: {
      onCommentAdded?: (comment: ColComment) => void;
      onCommentEdited?: (comment: ColComment) => void;
    }
  ): Promise<() => void> {
    const channelName = `presence-post-${adminId}-channel-${channelId}`;
    const channel = await this.subscribeInternal(channelName, false, true);
    const handleCommentAdded = (comment: ColComment) => {
      callbacks.onCommentAdded?.(comment);
    };
    const handleCommentEdited = (comment: ColComment) => {
      callbacks.onCommentEdited?.(comment);
    };
    channel.bind(PUSHER_EVENTS.COMMENT_ADDED, handleCommentAdded);
    channel.bind(PUSHER_EVENTS.COMMENT_EDITED, handleCommentEdited);
    return () => {
      channel.unbind(PUSHER_EVENTS.COMMENT_ADDED, handleCommentAdded);
      channel.unbind(PUSHER_EVENTS.COMMENT_EDITED, handleCommentEdited);
      this.unsubscribeFromCollabChannel(adminId, channelId);
    };
  }
  unsubscribeChannel(channelId: string): void {
    if (this.pusher) {
      return this.pusher.unsubscribe(channelId);
    }
  }
  unsubscribeFromCollabChannel(adminId: string, channelId: string) {
    if (this.pusher) {
      return this.pusher.unsubscribe(
        `presence-post-${adminId}-channel-${channelId}`
      );
    }
  }

  async subscribeToGeneralChannelCallbacks(
    adminId: string,
    callbacks: {
      onUnread?: (event: NewGeneralCommentEvent) => void;
      onStatusChanged?: (event: PostStatusChangeEvent) => void;
    }
  ): Promise<() => void> {
    const channelName = `presence-post-${adminId}`;
    const channel = await this.subscribeInternal(channelName, false, true);
    const handleCommentAdded = (event: NewGeneralCommentEvent) => {
      callbacks.onUnread?.(event);
    };
    const handleStatusChangedEvent = (event: PostStatusChangeEvent) => {
      callbacks.onStatusChanged?.(event);
    };
    channel.bind(PUSHER_EVENTS.COMMENT_ADDED, handleCommentAdded);
    channel.bind(PUSHER_EVENTS.STATUS_CHANGED, handleStatusChangedEvent);
    return () => {
      channel.unbind(PUSHER_EVENTS.COMMENT_ADDED, handleCommentAdded);
      channel.unbind(PUSHER_EVENTS.STATUS_CHANGED, handleStatusChangedEvent);
    };
  }

  unsubscribeFromGeneralChannel(adminId: string) {
    if (this.pusher) {
      return this.pusher.unsubscribe(`presence-post-${adminId}`);
    }
  }

  async getComments(channelId: string) {
    return skedApi.get(`/collaboration-channels/${channelId}/comments`);
  }

  async addComment(channelId: string, payload: CommentPayload) {
    return skedApi.post(
      `/collaboration-channels/${channelId}/comments`,
      payload
    );
  }

  async getUnreads(channelIds: string[] = []): Promise<{
    data: { channelId: string; unreads: number; mentioned: boolean }[];
  }> {
    return skedApi.get(`/collaboration-channels/unreads`, {
      params: {
        ...(channelIds.length > 0 ? { channelIds: channelIds.join(",") } : {}),
      },
    });
  }

  async getCollaborators() {
    return skedApi.get(`/collaboration-channels/users`);
  }

  async editComment(
    channelId: string,
    commentId: string,
    payload: { body: string; visibility: ColCommentVisibility }
  ) {
    return skedApi.put(
      `/collaboration-channels/${channelId}/comments/${commentId}`,
      payload
    );
  }

  async deleteComment(channelId: string, commentId: string) {
    return skedApi.delete(
      `/collaboration-channels/${channelId}/comments/${commentId}`
    );
  }
}

const api = new CollaborationApi();

export default api;

type CommentPayload = {
  body: string;
  metadata: {
    post: {
      postId: string;
    };
    postType: string;
  };
  visibility: ColCommentVisibility;
};

type NewGeneralCommentEvent = {
  channelId: string;
  createdBy: string;
};

type PostStatusChangeEvent = {
  postType: string;
  post: {
    postId: string;
    message: string;
    channel: string;
    currentStatus: PostStatus;
    newStatus: PostStatus;
  };
};

type PostStatus = {
  index: number;
  label: string;
  isPublishingAllowed: boolean;
  color: string;
  key: string;
  isDefault: boolean;
};

export type PolicyEnforcementPayload = {
  accountId: string;
  message: string;
  status: PolicyEnforcementStatus;
};
